import { TRIP_TYPE, type TripType } from '@ixigo/cab-core';
import type { StateCreator } from 'zustand';
import type {
	FormValidationResult,
	SearchFormActions,
	SearchFormData,
	SearchFormState,
} from './types';

export interface SearchFormSlice {
	searchForm: SearchFormState;
	searchFormActions: SearchFormActions;
}

const initialState: SearchFormState = {
	formData: {
		dateTime: '',
		// New users default to "To Airport" (drop to airport)
		tripType: TRIP_TYPE.AIRPORT_DROP_OFF,
	},
};

/**
 * Search form slice managing pickup, destination, date/time and form validation
 */
export const createSearchFormSlice: StateCreator<
	SearchFormSlice,
	[],
	[],
	SearchFormSlice
> = (set, get) => ({
	searchForm: initialState,

	searchFormActions: {
		updateFormData: (data: Partial<SearchFormData>) => {
			set((state) => ({
				searchForm: {
					...state.searchForm,
					formData: { ...state.searchForm.formData, ...data },
				},
			}));
			return get().searchForm.formData;
		},

		setTripType: (tripType: TripType) => {
			set((state) => {
				const { formData } = state.searchForm;
				if (formData.tripType === tripType) {
					return state;
				}
				// Cross-cutting rule: toggling trip type swaps From ↔ To values,
				// whether fields are filled or empty
				return {
					searchForm: {
						...state.searchForm,
						formData: {
							...formData,
							tripType,
							pickup: formData.drop,
							drop: formData.pickup,
						},
					},
				};
			});
			return get().searchForm.formData;
		},

		validateForm: (): FormValidationResult => {
			const { formData } = get().searchForm;

			if (!formData.pickup) {
				return {
					isValid: false,
					errorMessage: 'Please select pickup address',
				};
			}

			if (!formData.drop) {
				return {
					isValid: false,
					errorMessage: 'Please select destination address',
				};
			}

			if (!formData.dateTime?.trim()) {
				return {
					isValid: false,
					errorMessage: 'Please select preferred time',
				};
			}

			const completeData: Required<SearchFormData> = {
				pickup: formData.pickup,
				drop: formData.drop,
				dateTime: formData.dateTime,
				tripType: formData.tripType,
			};

			return {
				isValid: true,
				data: completeData,
			};
		},
	},
});
